import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { SWRConfig, type Cache } from 'swr'
import { useT } from '@/components/visitone/visitone-provider'
import { canView, landing } from '@/lib/access'
import type { Access } from '@/lib/bff/access'
import type { Tenant } from '@/lib/bff/tenants'
import { destinationFromPath, pathForDestination } from '@/lib/routing'
import { discardIdleRestore, openTenant } from '@/lib/tenant/tenant-session'

/*
 * The current tenant (VIS-326). TenantGate establishes it before this provider
 * mounts, so inside the shell there is always exactly one current tenant while
 * `phase` is `ready`. A confirmed switch removes the tenant's content at once
 * (`switching`), re-checks the target against a fresh tenant list, and either
 * arrives in the new tenant or fails with no tenant shown (`failed`).
 *
 * Every tenant gets its own SWR cache (`TenantDataBoundary`), discarded whole
 * on switch, so a late response for the previous tenant can never render.
 *
 * The caller's access decision in the tenant (VIS-329) is part of the tenant:
 * it loads on mount (`accessPhase`), and a switch loads the target's before it
 * arrives, so `access` changes in the same update as `currentTenant`. A switch
 * whose access cannot be loaded fails like any other failed switch.
 */

export type TenantPhase = 'ready' | 'switching' | 'failed'
export type AccessPhase = 'loading' | 'error' | 'ready'

type TenantContextValue = {
  /** The tenant being acted in; null while switching or after a failed switch. */
  currentTenant: Tenant | null
  currentTenantId: string | null
  tenants: Tenant[]
  phase: TenantPhase
  /** The tenant a switch is heading to. */
  target: Tenant | null
  /** The tenant a switch started from. */
  from: Tenant | null
  /** Starts a confirmed switch. */
  requestSwitch: (to: Tenant) => void
  retry: () => void
  goBack: () => void
  announcement: string
  /** Changes on every arrival in a tenant, so the shell can move focus. */
  arrival: number
  cache: Cache
  /** The caller's access decision in `currentTenant`; null until it has loaded. */
  access: Access | null
  accessPhase: AccessPhase
  /** Loads the current tenant's access again after it failed. */
  reloadAccess: () => void
  /**
   * Re-checks the current tenant's access after a change that may affect it,
   * keeping the shell mounted: the decision is replaced only when the new one
   * loads, and kept when it can't be loaded.
   */
  refreshAccess: () => void
}

const TenantContext = React.createContext<TenantContextValue | null>(null)

type Session = { sub: string; sid: string }

export function TenantProvider({
  initialTenant,
  tenants: initialTenants,
  session,
  refresh,
  loadAccess,
  onLost,
  announceOnMount = false,
  children,
}: {
  initialTenant: Tenant
  tenants: Tenant[]
  session: Session
  /** Fetches a fresh tenant list from the BFF; rejects on failure. */
  refresh: () => Promise<Tenant[]>
  /** Fetches the caller's access decision in a tenant; rejects on failure. */
  loadAccess: (tenantId: string) => Promise<Access>
  /** Going back found the previous tenant no longer allowed; the gate re-resolves. */
  onLost: () => void
  announceOnMount?: boolean
  children: React.ReactNode
}) {
  const t = useT()
  const navigate = useNavigate()
  const location = useLocation()
  const pathnameRef = React.useRef(location.pathname)
  pathnameRef.current = location.pathname

  const [state, setState] = React.useState<{
    current: Tenant | null
    phase: TenantPhase
    target: Tenant | null
    from: Tenant | null
    cache: Cache
    arrival: number
    access: Access | null
    accessPhase: AccessPhase
  }>(() => ({
    current: initialTenant,
    phase: 'ready',
    target: null,
    from: null,
    cache: new Map(),
    arrival: 0,
    access: null,
    accessPhase: 'loading',
  }))
  const [tenants, setTenants] = React.useState(initialTenants)
  const [announcement, setAnnouncement] = React.useState('')
  const attempt = React.useRef(0)

  const loadCurrentAccess = React.useCallback(
    async (tenantId: string) => {
      const id = ++attempt.current
      setState((prev) => ({ ...prev, access: null, accessPhase: 'loading' }))
      try {
        const access = await loadAccess(tenantId)
        if (id !== attempt.current) return
        setState((prev) => ({ ...prev, access, accessPhase: 'ready' }))
      } catch {
        if (id !== attempt.current) return
        setState((prev) => ({ ...prev, access: null, accessPhase: 'error' }))
      }
    },
    [loadAccess],
  )

  React.useEffect(() => {
    void loadCurrentAccess(initialTenant.id)
    if (announceOnMount) setAnnouncement(t('core.tenant.arrived', { to: initialTenant.name }))
    // Load the first tenant's access and announce the sign-in arrival once, after the live region exists.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const run = React.useCallback(
    async (to: Tenant, from: Tenant | null, goingBack: boolean) => {
      const id = ++attempt.current
      setAnnouncement('')
      setState((prev) => ({ ...prev, current: null, phase: 'switching', target: to, from }))

      let fresh: Tenant[] | null = null
      try {
        fresh = await refresh()
      } catch {
        fresh = null
      }
      if (id !== attempt.current) return

      const next = fresh?.find((tenant) => tenant.id === to.id)
      if (fresh) setTenants(fresh)
      if (!next) {
        if (fresh && goingBack) {
          onLost()
          return
        }
        setState((prev) => ({ ...prev, phase: 'failed' }))
        return
      }

      let access: Access
      try {
        access = await loadAccess(next.id)
      } catch {
        if (id !== attempt.current) return
        setState((prev) => ({ ...prev, phase: 'failed' }))
        return
      }
      if (id !== attempt.current) return

      openTenant({ ...session, tenantId: next.id })
      // The user has left any idle-restored tenant, even if the held write failed.
      discardIdleRestore()
      const destination = destinationFromPath(pathnameRef.current)
      const arriveAt = canView(access, destination) ? destination : landing(access)
      navigate(arriveAt ? pathForDestination(arriveAt) : '/', { replace: true })
      setState((prev) => ({
        current: next,
        phase: 'ready',
        target: null,
        from: null,
        cache: new Map(),
        arrival: prev.arrival + 1,
        access,
        accessPhase: 'ready',
      }))
      setAnnouncement(t('core.tenant.arrived', { to: next.name }))
    },
    [loadAccess, navigate, onLost, refresh, session, t],
  )

  const requestSwitch = React.useCallback(
    (to: Tenant) => {
      if (state.phase !== 'ready' || !state.current || to.id === state.current.id) return
      void run(to, state.current, false)
    },
    [run, state],
  )

  const retry = React.useCallback(() => {
    if (state.target) void run(state.target, state.from, false)
  }, [run, state])

  const goBack = React.useCallback(() => {
    if (state.from) void run(state.from, state.from, true)
  }, [run, state])

  const reloadAccess = React.useCallback(() => {
    if (state.phase === 'ready' && state.current) void loadCurrentAccess(state.current.id)
  }, [loadCurrentAccess, state])

  const refreshAccess = React.useCallback(() => {
    if (state.phase !== 'ready' || !state.current) return
    const tenantId = state.current.id
    const id = attempt.current
    loadAccess(tenantId).then(
      (access) => {
        if (id !== attempt.current) return
        setState((prev) => (prev.current?.id === tenantId && prev.accessPhase === 'ready' ? { ...prev, access } : prev))
      },
      () => undefined,
    )
  }, [loadAccess, state])

  const value = React.useMemo<TenantContextValue>(
    () => ({
      currentTenant: state.current,
      currentTenantId: state.current?.id ?? null,
      tenants,
      phase: state.phase,
      target: state.target,
      from: state.from,
      requestSwitch,
      retry,
      goBack,
      announcement,
      arrival: state.arrival,
      cache: state.cache,
      access: state.access,
      accessPhase: state.accessPhase,
      reloadAccess,
      refreshAccess,
    }),
    [state, tenants, requestSwitch, retry, goBack, reloadAccess, refreshAccess, announcement],
  )

  // SWR drops a provider cache's state when the SWRConfig that first used it
  // unmounts. This childless owner renders before any boundary and lives as
  // long as the cache, so boundaries (such as the language slot moving into the
  // drawer) can mount and unmount freely.
  return (
    <TenantContext.Provider value={value}>
      <SWRConfig key={cacheKey(state.cache)} value={{ provider: () => state.cache }} />
      {children}
    </TenantContext.Provider>
  )
}

export function useTenantContext() {
  const context = React.useContext(TenantContext)
  if (!context) throw new Error('useTenantContext must be used within a TenantProvider')
  return context
}

/** The current tenant's id inside tenant-scoped UI, where one is always open. */
export function useCurrentTenantId() {
  const { currentTenantId } = useTenantContext()
  if (!currentTenantId) throw new Error('useCurrentTenantId must be used inside the tenant data boundary')
  return currentTenantId
}

/*
 * Renders tenant-scoped UI against the current tenant's own SWR cache. Every
 * boundary for the same tenant shares one cache, owned by TenantProvider; a
 * switch replaces it.
 */
export function TenantDataBoundary({ children }: { children: React.ReactNode }) {
  const { cache } = useTenantContext()
  return (
    <SWRConfig key={cacheKey(cache)} value={{ provider: () => cache }}>
      {children}
    </SWRConfig>
  )
}

const cacheKeys = new WeakMap<Cache, number>()
let nextCacheKey = 0
function cacheKey(cache: Cache) {
  let key = cacheKeys.get(cache)
  if (key === undefined) {
    key = ++nextCacheKey
    cacheKeys.set(cache, key)
  }
  return key
}
